import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Users, Calendar, Dumbbell, TrendingUp, Plus, ArrowRight, Award, DollarSign,
  CheckCircle, Circle, Send, ClipboardList, Target, ChevronDown, ChevronUp,
  Zap, Star, Trophy, RefreshCw, Building2, Shield, ExternalLink, UserPlus, X, Check, Bell, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import CoachChallengeWidget from '@/components/challenge/CoachChallengeWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAthletes, setRecentAthletes] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [onboardingExpanded, setOnboardingExpanded] = useState(true);
  
  // Coach change requests
  const [changeRequests, setChangeRequests] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);

  const fetchChangeRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coach/change-requests`, { headers: getAuthHeaders() });
      setChangeRequests(res.data || []);
    } catch (error) {
      console.error('Failed to fetch change requests:', error);
    }
  }, [getAuthHeaders]);

  const handleRespondToRequest = async (requestId, accept) => {
    setProcessingRequest(requestId);
    try {
      const res = await axios.post(
        `${API}/coach/change-request/${requestId}/respond?accept=${accept}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      // Remove from list
      setChangeRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar solicitação');
    }
    setProcessingRequest(null);
  };

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [statsRes, athletesRes, teamsRes] = await Promise.all([
        axios.get(`${API}/coach/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/athletes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/coach/team-assignments`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setRecentAthletes(athletesRes.data.slice(0, 5));
      setTeamAssignments(teamsRes.data || []);
      
      // Also fetch change requests
      await fetchChangeRequests();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
    setLoading(false);
    setRefreshing(false);
  }, [getAuthHeaders, fetchChangeRequests]);

  // Fetch data on mount and when navigating back to dashboard
  useEffect(() => {
    fetchData();
  }, [fetchData, location.key]);

  // Also refresh when window regains focus (user switches tabs)
  useEffect(() => {
    const handleFocus = () => {
      fetchData(true);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  // Onboarding checklist items
  const onboardingChecklist = [
    { 
      id: 'athlete', 
      label: 'Invite your first athlete', 
      completed: (stats?.athlete_count || 0) > 0,
      link: '/coach/athletes',
      linkText: 'Invite Athlete'
    },
    { 
      id: 'drill', 
      label: 'Add drills to your library', 
      completed: (stats?.drill_count || 0) > 0,
      link: '/coach/drills',
      linkText: 'View Drills'
    },
    { 
      id: 'plan', 
      label: 'Create a training plan', 
      completed: (stats?.training_plan_count || 0) > 0,
      link: '/coach/training-plans',
      linkText: 'Create Plan'
    },
    { 
      id: 'pass', 
      label: 'Send first Athlete Pass', 
      completed: (stats?.athlete_passes?.active_count || 0) > 0,
      link: '/coach/athletes',
      linkText: 'Send Pass'
    },
  ];

  const completedSteps = onboardingChecklist.filter(item => item.completed).length;
  const onboardingProgress = (completedSteps / onboardingChecklist.length) * 100;
  const isOnboardingComplete = completedSteps === onboardingChecklist.length;

  // Coach Status Level
  const getCoachLevel = () => {
    const athleteCount = stats?.athlete_count || 0;
    if (athleteCount >= 15) return { level: 'Established Coach', icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-500/10' };
    if (athleteCount >= 5) return { level: 'Growing Coach', icon: Star, color: 'text-blue-400', bg: 'bg-blue-500/10' };
    return { level: 'Starter Coach', icon: Zap, color: 'text-muted-foreground', bg: 'bg-muted/30' };
  };

  const coachLevel = getCoachLevel();

  const statCards = [
    { label: 'Athletes', value: stats?.athlete_count || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Sessions', value: stats?.session_count || 0, icon: Calendar, color: 'text-green-400' },
    { label: 'Drills', value: stats?.drill_count || 0, icon: Dumbbell, color: 'text-purple-400' },
    { label: 'Training Plans', value: stats?.training_plan_count || 0, icon: TrendingUp, color: 'text-orange-400' },
  ];

  // Quick Actions
  const quickActions = [
    { label: 'Invite Athlete', icon: Users, link: '/coach/athletes', color: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400' },
    { label: 'Create Plan', icon: ClipboardList, link: '/coach/training-plans', color: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400' },
    { label: 'Schedule Session', icon: Calendar, link: '/coach/sessions', color: 'bg-green-500/10 hover:bg-green-500/20 text-green-400' },
    { label: 'Send Pass', icon: Send, link: '/coach/athletes', color: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-x-hidden" data-testid="coach-dashboard">
      {/* Header with Coach Level */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl uppercase tracking-tight truncate">Dashboard</h1>
            <p className="text-muted-foreground text-xs sm:text-sm md:text-base truncate">Welcome back, {user?.full_name}</p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-muted-foreground hover:text-primary flex-shrink-0"
            title="Refresh data"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-sm self-start md:self-auto flex-shrink-0 ${coachLevel.bg}`}>
          <coachLevel.icon className={coachLevel.color} size={16} />
          <span className={`text-xs sm:text-sm font-medium ${coachLevel.color}`}>{coachLevel.level}</span>
        </div>
      </div>

      {/* Weekly Challenge widget — viral engagement loop */}
      <CoachChallengeWidget />

      {/* Onboarding Checklist - Only show if not complete */}
      {!isOnboardingComplete && (
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-sm overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer"
            onClick={() => setOnboardingExpanded(!onboardingExpanded)}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <Target className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Get Started</h3>
                <p className="text-sm text-muted-foreground">Complete your setup to unlock all features</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-primary">{completedSteps}/{onboardingChecklist.length} completed</p>
                <div className="w-32 h-2 bg-muted/50 rounded-full overflow-hidden mt-1">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${onboardingProgress}%` }}
                  />
                </div>
              </div>
              {onboardingExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
          
          {onboardingExpanded && (
            <div className="px-4 pb-4 space-y-2">
              {onboardingChecklist.map((item) => (
                <div 
                  key={item.id}
                  className={`flex items-center justify-between p-3 rounded-sm transition-colors ${
                    item.completed ? 'bg-green-500/10' : 'bg-card'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {item.completed ? (
                      <CheckCircle className="text-green-400" size={20} />
                    ) : (
                      <Circle className="text-muted-foreground" size={20} />
                    )}
                    <span className={item.completed ? 'text-green-400' : ''}>{item.label}</span>
                  </div>
                  {!item.completed && (
                    <Link to={item.link}>
                      <Button size="sm" variant="outline" className="text-xs">
                        {item.linkText}
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Coach Change Requests Notifications */}
      {changeRequests.length > 0 && (
        <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-sm p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Bell className="text-blue-400" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-blue-400">New Athlete Requests</h3>
              <p className="text-sm text-muted-foreground">Athletes want you as their primary coach</p>
            </div>
          </div>
          
          <div className="space-y-2">
            {changeRequests.map((request) => (
              <div 
                key={request.id}
                className="bg-card border border-border/40 rounded-sm p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-bold">{request.athlete_name?.[0]}</span>
                    </div>
                    <div>
                      <p className="font-medium">{request.athlete_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Coach atual: {request.current_coach_name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                      onClick={() => handleRespondToRequest(request.id, false)}
                      disabled={processingRequest === request.id}
                    >
                      <X size={16} className="mr-1" />
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleRespondToRequest(request.id, true)}
                      disabled={processingRequest === request.id}
                    >
                      <Check size={16} className="mr-1" />
                      Accept
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {quickActions.map((action, idx) => (
          <Link 
            key={idx} 
            to={action.link}
            className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-sm border border-border/40 transition-colors min-h-[48px] ${action.color}`}
          >
            <action.icon size={18} />
            <span className="font-medium text-xs md:text-sm">{action.label}</span>
          </Link>
        ))}
      </div>

      {/* My Teams Section - Only show if coach has team assignments */}
      {teamAssignments.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-500/5 to-emerald-500/10 border border-emerald-500/30 rounded-sm overflow-hidden" data-testid="my-teams-section">
          <div className="p-4 border-b border-emerald-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Building2 className="text-emerald-400" size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-lg uppercase">My Teams</h3>
                  <p className="text-xs text-muted-foreground">You're also assigned to {teamAssignments.length} team{teamAssignments.length > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {teamAssignments.map((team) => (
              <div 
                key={team.team_id}
                className="flex items-center justify-between p-3 bg-card border border-border/40 rounded-sm hover:border-emerald-500/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
                    <Shield className="text-emerald-400" size={18} />
                  </div>
                  <div>
                    <p className="font-medium">{team.team_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {team.club_name} • {team.category} • {team.athletes_count} players
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-sm ${
                    team.role === 'head_coach' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {team.role === 'head_coach' ? 'Head Coach' : 'Assistant'}
                  </span>
                  <Button 
                    size="sm"
                    onClick={async () => {
                      try {
                        // Call context switch endpoint
                        const response = await axios.post(
                          `${API}/clubs/coach/switch-to-team`,
                          { team_id: team.team_id },
                          { headers: getAuthHeaders() }
                        );
                        
                        // Store team auth data
                        localStorage.setItem('team_token', response.data.token);
                        localStorage.setItem('team_user', JSON.stringify(response.data.user));
                        localStorage.setItem('team_teams', JSON.stringify(response.data.teams));
                        
                        // Navigate to team workspace
                        navigate(`/team/${team.team_id}`);
                      } catch (error) {
                        console.error('Failed to switch context:', error);
                        toast.error('Failed to switch to team context');
                      }
                    }}
                    className="bg-emerald-500 text-black hover:bg-emerald-400"
                  >
                    <ExternalLink size={14} className="mr-1" />
                    Open
                  </Button>
                </div>
              </div>
            ))}
            <div className="text-center pt-2">
              <p className="text-xs text-muted-foreground">
                Switch between your private coaching and team management contexts
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid - Responsive grid instead of horizontal scroll */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        {statCards.map((stat, idx) => (
          <div key={idx} className="bg-card border border-border/40 p-3 sm:p-4 md:p-5 rounded-sm">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <stat.icon className={stat.color} size={18} />
            </div>
            <p className="font-heading text-xl sm:text-2xl md:text-3xl mb-1">{stat.value}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Growth Mission Banner */}
      {stats?.athlete_passes?.passes_until_free > 0 && (
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30 p-3 md:p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Target className="text-primary" size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-xs md:text-sm uppercase text-primary">Growth Mission</p>
              <p className="text-xs md:text-sm text-muted-foreground">
                Grow your athlete base to unlock your free subscription. 
                <span className="text-primary font-medium ml-1">
                  {stats?.athlete_passes?.passes_until_free} more Athlete Passes to go!
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Coach Growth Insights & Athlete Pass */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Athlete Pass Stats - Coach Advantage Progress */}
        <div className="bg-card border border-border/40 p-5 rounded-sm">
          <div className="flex items-center gap-3 mb-4">
            <Award className="text-primary" size={22} />
            <h3 className="font-heading text-lg uppercase">Athlete Pass</h3>
          </div>
          <div className="mb-4">
            <p className="text-3xl font-heading text-primary">{stats?.athlete_passes?.active_count || 0}</p>
            <p className="text-xs text-muted-foreground">Active Passes</p>
            <p className="text-xs text-muted-foreground mt-1 italic">
              Parents pay ${stats?.athlete_passes?.price_per_pass || '9.99'}/athlete/month to the platform
            </p>
          </div>
          
          {/* Coach Advantage Progress */}
          {stats?.athlete_passes?.passes_until_free > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Coach Advantage Progress</span>
                <span className="text-primary font-medium">
                  {stats?.athlete_passes?.active_count || 0}/10
                </span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${((stats?.athlete_passes?.active_count || 0) / 10) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="text-primary font-semibold">{stats?.athlete_passes?.passes_until_free}</span> more passes for FREE subscription
              </p>
            </div>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3">
              <p className="text-sm text-green-400 font-semibold flex items-center gap-2">
                <Trophy size={16} />
                Coach Advantage Active - Free subscription!
              </p>
            </div>
          )}
        </div>

        {/* Coach Level & Subscription */}
        <div className="bg-card border border-border/40 p-5 rounded-sm">
          {/* Coach Level Badge */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg uppercase">Your Status</h3>
            <span className={`px-3 py-1.5 rounded-sm text-xs uppercase font-bold tracking-wider ${
              stats?.coach_level?.level === 'elite' 
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                : stats?.coach_level?.level === 'growth'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-muted/30 text-muted-foreground border border-border/40'
            }`}>
              {stats?.coach_level?.label || 'Starter Coach'}
            </span>
          </div>
          
          {/* Level Progress */}
          {stats?.coach_level?.next_level && (
            <div className="mb-4 p-3 bg-muted/20 rounded-sm">
              <p className="text-xs text-muted-foreground mb-1">
                Level up to <span className="text-primary font-medium capitalize">{stats?.coach_level?.next_level} Coach</span>
              </p>
              <p className="text-sm">
                <span className="text-primary font-semibold">{stats?.coach_level?.athletes_to_next}</span> more athletes to go
              </p>
            </div>
          )}

          {/* Subscription Info */}
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-primary/10 text-primary rounded text-sm uppercase font-semibold">
              Pro
            </span>
            <span className="px-3 py-1 rounded text-sm uppercase bg-primary/10 text-primary border border-primary/30 flex items-center gap-1">
              <Sparkles size={11} /> Founding Beta
            </span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-muted-foreground">Monthly Fee</p>
              <div className="flex items-baseline gap-1 opacity-50 mt-1">
                <span className="text-sm text-muted-foreground line-through">$</span>
                <span className="font-heading text-2xl text-muted-foreground line-through decoration-2">
                  {stats?.subscription?.plan_price?.toFixed(2) || '14.99'}
                </span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded-sm px-2.5 py-1">
                <Sparkles size={10} className="text-primary" />
                <span className="text-[10px] uppercase tracking-widest text-primary font-bold">Included during Founding Beta</span>
              </div>
            </div>
          </div>
          
          {/* No upgrade prompts during Founding Beta — all features unlocked */}
          {false && stats?.subscription?.plan?.toLowerCase() !== 'pro' && !stats?.subscription?.coach_advantage_active && (
            <div className="mb-4">
              <Button 
                onClick={() => navigate('/coach/settings?tab=subscription')}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold uppercase tracking-wider"
                data-testid="upgrade-to-pro-btn"
              >
                <Zap size={16} className="mr-2" />
                Upgrade to Pro
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Unlock white-label & financial dashboard
              </p>
            </div>
          )}
          
          {/* Growth Insight */}
          {(stats?.athlete_count || 0) < 15 && (
            <div className="bg-muted/30 rounded-sm p-3 text-xs text-muted-foreground">
              <p className="flex items-center gap-2">
                <TrendingUp size={14} className="text-primary" />
                <span>Top coaches typically manage <span className="text-primary font-semibold">15+ athletes</span>. You're on your way!</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Athletes */}
      <div className="bg-card border border-border/40 rounded-sm">
        <div className="p-5 border-b border-border/40 flex items-center justify-between">
          <h3 className="font-heading text-lg uppercase">Recent Athletes</h3>
          <Link to="/coach/athletes" className="text-primary text-sm uppercase tracking-wider hover:underline flex items-center gap-1">
            View All <ArrowRight size={16} />
          </Link>
        </div>
        
        {recentAthletes.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-3">No athletes yet</p>
            <Link to="/coach/athletes">
              <Button variant="outline" size="sm">Add Your First Athlete</Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {recentAthletes.map((athlete) => (
              <Link 
                key={athlete.id}
                to={`/coach/athletes/${athlete.id}`}
                className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">{athlete.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium">{athlete.name}</p>
                    <p className="text-xs text-muted-foreground">{athlete.position} • {athlete.age} years</p>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground" size={18} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Sticky CTA - Send Athlete Pass */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border/40 p-4 md:hidden z-50">
        <Link to="/coach/athletes" className="block">
          <Button className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold min-h-[48px]">
            <Send className="mr-2 h-4 w-4" />
            Send Athlete Pass
          </Button>
        </Link>
      </div>

      {/* Bottom spacing for mobile sticky CTA */}
      <div className="h-20 md:hidden" />
    </div>
  );
};

export default CoachDashboard;
